<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Manage user's organization</h1>

                    <a href="/#/admin/organizations/manage" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        Back
                    </a>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="id && user" class="container">
            <h4 class="fg-color-blue">{{displayName}} belongs to organization {{organizationUnitName}}</h4>
            <br />

            <table class="table table-striped table-hover">
                <tbody>
                    <tr v-for="ou in organizationUnits">
                        <th>{{ou.Name}}</th>
                        <td>
                            <span v-if="ou.OrganizationUnitId==organizationUnitId">
                                <i class="fa fa-check-circle fg-color-green" aria-hidden="true"></i>
                                Current
                            </span>
                            <button v-else @click="handleChangeOrganizationUnitCommandV1(ou)"
                                    type="button" class="btn btn-sm btn-primary">
                                Move to
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-else-if="!id" class="container">
            <div class="input-group mb-3">
                <input v-model="emailinput" type="email" class="form-control" placeholder="Find user by email">
                <div class="input-group-append">
                    <button @click="handleFindUser" class="btn btn-outline-secondary" type="button">Find</button>
                </div>
            </div>
        </div>

        <div v-else class="container">
            <h4>Email '{{id}}' not found</h4>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, onMounted } from 'vue'
    import { useRoute } from 'vue-router'
    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'
    import { useUserQueryV1 } from '@Organizations/Users/queries-vue-composables'
    import { useOrganizationUnitsQueryV1 } from '@Organizations/OrganizationUnits/queries-vue-composables'
    import { useChangeOrganizationUnitCommandV1 } from '@Organizations/Users/commands-vue-composables'

    // ---------------- route ----------------

    const route = useRoute()

    const id = computed(() =>
        typeof route.params.id === 'string'
            ? route.params.id
            : null
    )

    // ---------------- local state ----------------

    const emailinput = ref('')

    // ---------------- queries ----------------

    const {
        modelUserQueryV1: user,
        runUserQueryV1
    } = useUserQueryV1(id)

    const {
        modelOrganizationUnitsQueryV1: organizationUnitsList,
        runOrganizationUnitsQueryV1
    } = useOrganizationUnitsQueryV1()

    // ---------------- command ----------------

    const {
        invokeChangeOrganizationUnitCommandV1,
        onSucceedChangeOrganizationUnitCommandV1
    } = useChangeOrganizationUnitCommandV1(id)

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        document.title = "Manage user's organization"
        runOrganizationUnitsQueryV1()
    })

    // ---------------- react to route param ----------------

    watch(id, (newId) =>
    {
        if (newId)
        {
            runUserQueryV1()
        }
    }, { immediate: true })

    // ---------------- derived ----------------
    
    const displayName = computed(() => user.value?.DisplayName)
    const email = computed(() => user.value?.Email)
    const organizationUnitId = computed(() => user.value?.OrganizationUnitId)
    const organizationUnits = computed(() => organizationUnitsList.value?.OrganizationUnits ?? [])

    const organizationUnit = computed(() =>
        organizationUnits.value.find(
            (ou: any) => ou.OrganizationUnitId === organizationUnitId.value
        )
    )

    const organizationUnitName = computed(() => organizationUnit.value?.Name)

    // ---------------- handlers ----------------

    function handleChangeOrganizationUnitCommandV1(ou: any)
    {
        if (!id.value) return

        if (confirm(`Do you really want to change user ${email.value} organization to ${ou.Name}?`))
        {
            invokeChangeOrganizationUnitCommandV1({ OrganizationUnitId: ou.OrganizationUnitId })
        }
    }

    function handleFindUser()
    {
        if (!emailinput.value) return

        window.location.hash =
            `#/admin/organizations/users/manage/organization/${encodeURIComponent(emailinput.value)}`
    }

    onSucceedChangeOrganizationUnitCommandV1(() =>
    {
        window.location.reload();
    })
</script>
